'use client';

import React, { useState, useMemo } from 'react';
import { Menu } from '@mantine/core';
import { Habit } from '../types/dashboard';
import styles from './HabitCard.module.scss';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../api/ApiClientContext';
import { HabitTrackingModal } from '../../habitrackingModal/HabitTrackingModal';
import { ActionTypePickerResponse, ActionGetResponse } from '../../../api/Api';
import { ActionTypePickerModal } from '../../habitrackingModal/ActionTypePickerModal/ActionTypePickerModal';
import { ActionQuestionModal } from '../../habitrackingModal/ActionQuestionModal/ActionQuestionModal';

// Import habit icons
import OneIcon from '../../../assets/icons/habitIcons/OneIcon.svg';
import TwoIcon from '../../../assets/icons/habitIcons/TwoIcon.svg';
import ThreeIcon from '../../../assets/icons/habitIcons/ThreeIcon.svg';
import FourIcon from '../../../assets/icons/habitIcons/FourIcon.svg';
import { Icon } from '../../../assets/icons/Icon';

const habitIcons = [OneIcon, TwoIcon, ThreeIcon, FourIcon];

// Function to get a deterministic random icon based on habit id
const getRandomIcon = (habitId: string) => {
  const hash = habitId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return habitIcons[hash % habitIcons.length];
};

interface HabitCardProps {
  habit: Habit;
  programmeName?: string;
  className?: string;
  showAction?: boolean;
  actionTypes?: ActionTypePickerResponse;
}

export const HabitCard: React.FC<HabitCardProps> = ({ habit, programmeName, className = '', showAction, actionTypes }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isActionPickerOpen, setIsActionPickerOpen] = useState(false);
  const [isActionQuestionOpen, setIsActionQuestionOpen] = useState(false);
  const [, setSelectedActionTypeId] = useState<string | null>(null);
  const [actionData, setActionData] = useState<ActionGetResponse | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingLogDate, setPendingLogDate] = useState<string | null>(null);
  const [openPositiveImpactsOnMount, setOpenPositiveImpactsOnMount] = useState(false);
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  
  const completionTarget = habit.min_completion_days || habit.total_days || 30;
  const progress =
    completionTarget > 0 ? Math.min((habit.days_completed / completionTarget) * 100, 100) : 0;

  // Get random icon based on habit id
  const HabitIcon = useMemo(() => getRandomIcon(habit.id), [habit.id]);

  // Fetch full active habits data to get positive_changes
  const { data: activeHabits } = useQuery({
    queryKey: ['myActiveHabits'],
    queryFn: async () => {
      const res = await apiClient.api.getMyActiveHabitsApiV1HabitsMyActiveGet();
      return res?.data;
    },
  });

  const activeHabitData = activeHabits?.find((activeHabit: any) => activeHabit.id === habit.id);

  const habitData = (activeHabitData as any) || {
    id: habit.id,
    habit: {
      id: habit.id,
      name: habit.habit_name,
      description: (habit as any)?.description || '',
      instructions: (habit as any)?.instructions || '',
      difficulty: 'medium',
      estimated_time_minutes: null,
      is_custom: false,
      default_duration_days: 60,
      min_completion_days: 48,
      max_extension_days: 20,
      tips: null,
      icon_url: habit.habit_icon_url,
      behaviour_name: null,
      module_name: null,
      positive_changes: [],
    },
    started_at: (habit as any).started_at || new Date().toISOString(),
    target_completion_date: habit.target_end_date || new Date().toISOString(),
    completed_at: null,
    is_active: true,
    is_completed: false,
    completion_percentage: habit.progress_percentage,
    current_streak: habit.current_streak,
    total_logs: habit.days_completed,
    days_completed: habit.days_completed,
    days_remaining: habit.days_remaining,
    can_log_today: habit.can_log_today,
  };

  const handleTrackClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Always open the HabitTrackingModal first so user can select a date
    setIsModalOpen(true);
  };

  // Called when user clicks "Yes" in HabitTrackingModal for action-based habits
  // For action-based habits, no request is sent yet - we store the date and proceed to next modal
  const handleHabitLogSuccess = async (selectedDate: string | null) => {
    if (!showAction || !actionTypes || actionTypes.action_types.length === 0) return;

    setPendingLogDate(selectedDate);
    setIsModalOpen(false);

    if (actionTypes.action_types.length === 1) {
      // Single action type: auto-select and fetch action data directly
      const singleType = actionTypes.action_types[0];
      try {
        const res = await apiClient.api.getHabitActionApiV1HabitsHabitIdActionGet(
          activeHabitData?.habit?.id || habit.id,
          { action_type_id: singleType.id }
        );
        handleActionTypeSelect(singleType.id, res.data);
      } catch (error) {
        console.error('Error fetching action for single type:', error);
      }
    } else {
      // Multiple action types: show the picker
      setIsActionPickerOpen(true);
    }
  };

  const handleActionTypeSelect = async (actionTypeId: string, actionResponseData: ActionGetResponse) => {
    setSelectedActionTypeId(actionTypeId);
    setActionData(actionResponseData);
    setIsActionPickerOpen(false);

    if (actionResponseData.question_type === 'checkbox_with_followup') {
      setIsActionQuestionOpen(true);
    } else {
      // Unsupported question type in FE -- log the habit directly, skip action card
      try {
        await apiClient.api.logHabitApiV1HabitsUserHabitIdLogPost(habit.id, {
          log_date: pendingLogDate,
          log_type: 'completed',
        });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        queryClient.invalidateQueries({ queryKey: ['dashboardProgress'] });
        queryClient.invalidateQueries({ queryKey: ['habitById', habit.id] });
        queryClient.invalidateQueries({ queryKey: ['myActiveHabits'] });
        queryClient.invalidateQueries({ queryKey: ['habitProgress'] });
      } catch (error) {
        console.error('Error logging habit:', error);
      } finally {
        setActionData(null);
        setSelectedActionTypeId(null);
        setPendingLogDate(null);
      }
    }
  };

  const handleActionQuestionSubmit = async (data: {
    action_id: string;
    selected_options: number[];
    follow_up_answer?: string;
  }) => {
    setIsSubmitting(true);
    try {
      // First, log the habit with the stored date (this was deferred from the first modal)
      await apiClient.api.logHabitApiV1HabitsUserHabitIdLogPost(habit.id, {
        log_date: pendingLogDate,
        log_type: 'completed',
      });
      console.log('Habit logged for date:', pendingLogDate);
      
      // Then, submit the action response
      const res = await apiClient.api.submitActionResponseApiV1HabitsUserHabitIdActionResponsePost(
        habit.id, // user_habit_id
        {
          action_id: data.action_id,
          selected_options: data.selected_options,
          follow_up_answer: data.follow_up_answer,
        }
      );
      console.log('Action response submitted:', res.data);
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardProgress'] });
      queryClient.invalidateQueries({ queryKey: ['habitById', habit.id] });
      queryClient.invalidateQueries({ queryKey: ['myActiveHabits'] });
      queryClient.invalidateQueries({ queryKey: ['habitProgress'] });
      
      // Don't close the modal yet - let the completion screen handle it
      // The modal will be closed when user clicks "Done" or "Add impacts"
    } catch (error) {
      console.error('Error submitting action response:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleActionQuestionClose = () => {
    setIsActionQuestionOpen(false);
    setActionData(null);
    setSelectedActionTypeId(null);
    setPendingLogDate(null);
  };

  // Get programme name for badge (from prop or fallback to behaviour/module name)
  const badgeText = programmeName || (habit as any).behaviour_name || (habit as any).module_name || activeHabitData?.habit?.behaviour_name || activeHabitData?.habit?.module_name;

  return (
    <>
      <article
        className={`${styles.habitCard} ${className}`}
        aria-label={`Habit: ${habit.habit_name}`}
      >
        {/* Icon Section */}
        <div className={styles.habitCard__iconWrapper}>
          <img src={HabitIcon} alt="" className={styles.habitCard__icon} />
        </div>

        <div className={styles.habitCard__content}>

          <div className={styles.habitCard__topRow}>
            {badgeText && (
              <span className={styles.habitCard__badge}>{badgeText}</span>
            )}
            <div className={styles.habitCard__daysLeft}>
              <span className={styles.days_remaining}>{habit.days_remaining} DAYS LEFT</span>
              <Menu 
                shadow="md" 
                width={200}
                position="bottom-end"
                withinPortal
              >
                <Menu.Target>
                  <div 
                    role="button" 
                    className={styles.habitCard__menuIcon}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Icon iconName="TreeDotsIcon" />
                  </div>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item 
                    className={styles.habitCard__menuItem}
                    onClick={(e) => {
                      e.stopPropagation();
                      // TODO: Handle resign from habit
                      console.log('Resign from habit:', habit.id);
                    }}
                  >
                    <span className={styles.habitCard__resignText}>Resign from habit</span>
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </div>
          </div>

          {/* Title */}
          <h3 className={styles.habitCard__title}>{habit.habit_name}</h3>

          {/* Stats */}
          <div className={styles.habitCard__stats}>
            <span className={styles.habitCard__statsLabel}>Times tracked </span>
            <span className={styles.habitCard__statsNumber}>
              {habit.days_completed}/{completionTarget}
            </span>
          </div>

          {/* Progress Bar */}
          <div className={styles.habitCard__progressBarWrapper}>
            <div
              className={styles.habitCard__progressBar}
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Progress: ${progress}%`}
            >
              <div
                className={styles.habitCard__progressFill}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {/* Bottom Row: Streak + Track Button */}
          <div className={styles.habitCard__bottomRow}>
            <p className={styles.habitCard__streak}>
              {habit.current_streak > 0 
                ? `You've achieved an impressive ${habit.current_streak}-day streak! Do not lose it`
                : 'Start your streak today!'
              }
            </p>
            <button 
              className={styles.habitCard__trackBtn}
              onClick={handleTrackClick}
            >
              Track habit
            </button>
          </div>
        </div>
      </article>

      <HabitTrackingModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedActionTypeId(null);
          setOpenPositiveImpactsOnMount(false);
        }}
        habitData={habitData}
        habitTitle={activeHabitData?.habit?.name || habit.habit_name}
        habitBehavior={activeHabitData?.habit?.instructions || (habit as any)?.instructions}
        habitDescription={activeHabitData?.habit?.description || (habit as any)?.description}
        programmeName={badgeText}
        isActionBased={showAction && actionTypes && actionTypes.action_types.length >= 1}
        onLogSuccess={handleHabitLogSuccess}
        showPositiveImpacts={activeHabitData?.show_positive_impacts ?? true}
        openPositiveImpactsOnMount={openPositiveImpactsOnMount}
        preselectedDate={pendingLogDate}
      />

      {showAction && actionTypes && actionTypes.action_types.length >= 2 && /* picker only for 2+ types */ (
        <ActionTypePickerModal
          isOpen={isActionPickerOpen}
          onClose={() => setIsActionPickerOpen(false)}
          habitId={activeHabitData?.habit?.id || habit.id}
          habitName={habit.habit_name}
          actionTypes={actionTypes}
          onSelectActionType={handleActionTypeSelect}
        />
      )}

      {actionData && actionData.question_type === 'checkbox_with_followup' && (
        <ActionQuestionModal
          isOpen={isActionQuestionOpen}
          onClose={handleActionQuestionClose}
          habitName={habit.habit_name}
          actionData={actionData as any}
          onSubmit={handleActionQuestionSubmit}
          isLoading={isSubmitting}
          showPositiveImpacts={activeHabitData?.show_positive_impacts ?? false}
          onAddImpacts={() => {
            // Close the action question modal and open the positive impacts modal directly
            setIsActionQuestionOpen(false);
            setActionData(null);
            setSelectedActionTypeId(null);
            setOpenPositiveImpactsOnMount(true);
            setIsModalOpen(true);
          }}
        />
      )}
    </>
  );
};
